import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import core, { App } from '@gin-betting/core';
import { Pagination } from '@gin-betting/match-list';
import cd from 'debug';
import { OrderedSet } from 'immutable';
import { createStructuredSelector } from 'reselect';
import get from 'ts-get';

import { SmartLink } from 'components';
import globalMessages from 'messages';
import { checkIfNewsPage } from 'utils';
import { CustomVisibilitySensor } from '../CustomVisibilitySensor/CustomVisibilitySensor';
import SportEventRow, { StreamStub } from '../SportEventRow';

import './upcoming.scss';

const debug = cd('app:match-list:components:UpcomingMatchList');

const {
    entities: {
        selectors: { selectSports },
    },
} = core;

interface OwnProps {
    matchIds: OrderedSet<string>;
    isMedium: boolean;
    isSmall: boolean;
    handleVisibilityChange?: Pagination.ReturnedPaginationProps['handleVisibilityChange'];
    visibilityInitRef?: Pagination.ReturnedPaginationProps['visibilityInitRef'];
}

interface MappedProps {
    sports: ReturnType<typeof selectSports>;
}

type Props = OwnProps & MappedProps;

const {
    utils: { getBettingOptions },
} = core;

class UpcomingMatchList extends React.Component<Props> {
    getMatchIds = () => this.props.matchIds;

    getNewsSportNames = () => {
        const { sports } = this.props;
        const { selectedSportIds } = getBettingOptions();

        const hasTooManySports = selectedSportIds.length > 5;

        const hasNamesToRender =
            selectedSportIds.length && sports.size && !hasTooManySports;

        if (!hasNamesToRender) {
            return null;
        }

        let sportNames: JSX.Element[] = [];

        sportNames = selectedSportIds
            .map(sportId => sports.get(sportId))
            .filter((i): i is NonNullable<typeof i> => !!i)
            .map(({ id, name }, index) => (
                <span styleName="sport-name" key={id}>
                    {`${name}${
                        index + 1 === selectedSportIds.length ? '' : ','
                    }`}
                </span>
            ));

        sportNames.push(
            <span key="matches" styleName="sport-name">
                <FormattedMessage {...globalMessages.upcomingShort} />
            </span>
        );

        return <div styleName="tags-list">{sportNames}</div>;
    };

    render() {
        debug('render', this.props);
        const {
            matchIds,
            handleVisibilityChange,
            visibilityInitRef,
        } = this.props;

        if (!matchIds.size) return null;

        const sportEventRows: JSX.Element[] = [];

        const isNewsPage = checkIfNewsPage();

        let prevMatchId: string | undefined;

        matchIds.delete('visibility1').forEach((matchId: string) => {
            const component = matchId.match(/visibility/) ? (
                <CustomVisibilitySensor
                    key={matchId}
                    visibilityChange={handleVisibilityChange}
                    page={
                        +get(matchId.match(/\d+$/), item => item[0] ?? '0', '0')
                    }
                    visibilityInitRef={visibilityInitRef}
                />
            ) : (
                <SportEventRow
                    key={matchId}
                    matchId={matchId}
                    prevMatchId={prevMatchId}
                />
            );

            sportEventRows.push(component);

            prevMatchId = matchId.match(/visibility/) ? prevMatchId : matchId;
        });

        sportEventRows.push(<StreamStub key="streamStub" />);

        if (isNewsPage) {
            return (
                <React.Fragment>
                    <div styleName="news-container">
                        {this.getNewsSportNames()}

                        <div styleName="scrollable-container">
                            {sportEventRows}
                        </div>

                        <SmartLink to={App.Routes.UPCOMING_LIST}>
                            <div styleName="more-matches-container">
                                <div styleName="more-matches-button">
                                    <FormattedMessage
                                        {...globalMessages.more}
                                    />{' '}
                                    <FormattedMessage
                                        {...globalMessages.upcomingShort}
                                    />
                                </div>
                            </div>
                        </SmartLink>
                    </div>
                </React.Fragment>
            );
        }

        return sportEventRows;
    }
}

const mapStateToProps = createStructuredSelector({
    sports: selectSports,
});

export default connect(mapStateToProps)(UpcomingMatchList);
